import { Chart, registerables } from "chart.js"
import "chart.js/auto"
import _ from "lodash"
import { useEffect, useState } from "react"
import { Line } from "react-chartjs-2"
import {
  useBaselineScoresByPatients,
  usePatientsAge,
  usePatientsCount,
  useScoresByMonth,
  useStudyValues,
  useSubmssionsCount
} from "../../hooks/useGroupOverview"
import { IScore, StudyOverview } from "../../types/types"

Chart.register(...registerables)

const mapStudyName: Record<string, string> = {
  "sumo-centre-hospitalier-boulogne-gal": "Centre Hospitalier Boulogne",
  "clinique-herbert-alr": "Clinique Herbert",
  "clinique-dos-valmy-xch": "Clinique du dos Valmy",
  "institut-locomoteur-ouest-jle": "Institut Locomoteur Ouest",
  "centre-clinical-soyaux-kmo": "Centre clinical Soyaux",
  "hopital-prive-du-dos-mca": "Hôpital privé du dos",
  "polyclinique-parc-jfg": "Polyclinique du Parc"
}

const mapPostOpScores: Record<string, string> = {
  "@3days": "Scores @3 jours",
  "@15days": "Scores @15 jours",
  "@30days": "Scores @30 jours",
  "@3months": "Scores @3 mois",
  "@6months": "Scores @6 mois"
}

const StatsStudyGroupOverview = () => {
  const [chosenStudy, setChosenStudy] = useState<StudyOverview>()

  const { data: patientCount } = usePatientsCount(chosenStudy?.id)
  const { data: surveysAnsweredCount } = useSubmssionsCount(chosenStudy?.id)
  const { data: baselineScores } = useBaselineScoresByPatients(chosenStudy?.id)
  const { data: scoreByMonth } = useScoresByMonth(chosenStudy?.id)
  const { data: patientsAges } = usePatientsAge(chosenStudy?.id)
  const { data: studies } = useStudyValues()

  const [chartData, setChartData] = useState({
    labels: [] as Array<string>,
    datasets: [
      {
        label: "Évolution de la qualité de vie",
        data: [] as Array<number>,
        backgroundColor: ["rgba(6, 214, 160, 1)"],
        borderColor: ["rgba(6, 214, 160, 1)"],
        borderWidth: 2,
        tension: 0.8
      }
    ]
  })

  useEffect(() => {
    if (scoreByMonth) {
      const _data = _.omit(scoreByMonth, ["@6months"])

      const _labels = Object.keys(_data).map((key: string) => key)

      const _odi_values = Object.values(_data)
        .map((values: IScore[]) =>
          !isNaN(_.meanBy(values, "life_quality"))
            ? 100 - _.meanBy(values, "life_quality")
            : 0
        )
        .filter((val) => val !== undefined)

      const _pain_values = Object.values(_data)
        .map((values: IScore[]) =>
          !isNaN(_.meanBy(values, "pain_management"))
            ? _.meanBy(values, "pain_management")
            : 0
        )
        .filter((val) => val !== undefined)

      const _walk_values = Object.values(_data)
        .map((values: IScore[]) =>
          !isNaN(_.meanBy(values, "physical_autonomy"))
            ? _.meanBy(values, "physical_autonomy")
            : 0
        )
        .filter((val) => val !== undefined)

      setChartData({
        labels: _labels,
        datasets: [
          {
            label: "ODI",
            data: _odi_values,
            backgroundColor: ["rgba(6, 214, 160, 1)"],
            borderColor: ["rgba(6, 214, 160, 1)"],
            borderWidth: 2,
            tension: 0.4
          },
          {
            label: "Gestion Douleur",
            data: _pain_values,
            backgroundColor: ["rgba(239, 71, 111, 1)"],
            borderColor: ["rgba(239, 71, 111, 1)"],
            borderWidth: 2,
            tension: 0.4
          },
          {
            label: "Autonomie Physique",
            data: _walk_values,
            backgroundColor: ["rgba(33, 134, 215, 1)"],
            borderColor: ["rgba(33, 134, 215, 1)"],
            borderWidth: 2,
            tension: 0.4
          }
        ]
      })
    } else {
      setChartData({
        labels: [],
        datasets: []
      })
    }
  }, [scoreByMonth])

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target
    if (value === "-") {
      setChosenStudy(undefined)
    }
    setChosenStudy(studies?.find((study) => study.id === parseInt(value)))
  }

  const rendePostOpScoreColumn = () => {
    return scoreByMonth
      ? Object.entries(
          _.omit(scoreByMonth, [
            "@15days",
            "@45days",
            "@2months",
            "@3months",
            "@6months"
          ])
        ).map(([key, scores]) => (
          <div className="flex flex-col md:px-2">
            <div className="flex w-40">
              <div className="font-bold text-slate-500 mt-2 text-center">
                {mapPostOpScores[key]}
              </div>
            </div>
            <div className="flex flex-row md:flex-col">
              <div className="flex md:w-40 md:h-32 w-24 h-24 flex-col rounded-md md:px-4 md:py-4 bg-sumo-500 opacity-90 md:mt-4 mt-2 px-2 justify-center">
                <div className="font-bold md:text-5xl text-2xl text-slate-50">
                  {scores && !isNaN(_.meanBy(scores, "life_quality"))
                    ? (100 - _.meanBy(scores, "life_quality")).toPrecision(3)
                    : "..."}
                </div>
                <div className="font-bold text-slate-50 md:mt-2 text-xs">
                  ODI
                </div>
              </div>
              <div className="flex md:w-40 md:h-36 w-24 h-24 flex-col rounded-md md:px-4 md:py-4 bg-sumo-550 opacity-90 md:mt-4 mt-2 px-2 justify-center ml-2 md:ml-0">
                <div className="font-bold md:text-5xl text-2xl text-slate-50">
                  {scores && !isNaN(_.meanBy(scores, "physical_autonomy"))
                    ? _.meanBy(scores, "physical_autonomy").toPrecision(3)
                    : "..."}
                </div>
                <div className="font-bold text-slate-50 md:mt-2 text-xs">
                  Autonomie
                </div>
              </div>
              <div className="flex md:w-40 md:h-40 w-28 h-24 flex-col rounded-md md:px-4 md:py-4 bg-sumo-500 opacity-90 md:mt-4 mt-2 px-2 justify-center ml-2 md:ml-0">
                <div className="font-bold md:text-5xl text-2xl text-slate-50">
                  {scores && !isNaN(_.meanBy(scores, "pain_management"))
                    ? _.meanBy(scores, "pain_management").toPrecision(3)
                    : "..."}
                </div>
                <div className="font-bold text-slate-50 md:mt-2 text-xs">
                  Gestion douleur
                </div>
              </div>
            </div>
          </div>
        ))
      : null
  }

  return (
    <div className="flex w-full md:w-full bg-sumo-100 flex-1 flex-wrap  justify-center items-center">
      <div className="flex md:w-full flex-row justify-center items-center">
        <section className="flex pt-3 md:px-3 flex-col">
          <div className="flex flex-row py-4 md:w-full">
            <div className="text-slate-900 font-semibold md:w-1/2">
              STATISTIQUES PATIENTS
            </div>
            <div className="flex flex-row md:w-1/2 ml-4 items-center justify-end">
              <div className="px-2 w-1/4">Choix centre</div>
              <div className="px-2 w-1/2">
                <select
                  className="mt-2 border-2 border-cortexx-500 rounded-md p-2"
                  onChange={handleSelectChange}
                  id="center-select"
                >
                  <option
                    className="text-cortexx-900 text-sm"
                    key={"empty"}
                    value={"-"}
                    selected
                  >
                    {"-"}
                  </option>
                  {studies?.map((option) => (
                    <option
                      className="text-cortexx-900 text-sm"
                      key={option.id}
                      value={option.id}
                      selected
                    >
                      {mapStudyName[option.name] ?? option.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row">
            <div className="flex flex-col">
              <div className="flex w-40">
                <div className="font-bold text-slate-500 mt-2 text-center">
                  Plateforme SuMO
                </div>
              </div>
              <div className="flex flex-row md:flex-col">
                <div className="flex md:w-40 md:h-32 w-24 h-24 flex-col rounded-md md:px-4 md:py-4 bg-sumo-500 opacity-90 md:mt-4 mt-2 px-2 justify-center">
                  <div className="font-bold md:text-5xl text-2xl text-slate-50">
                    {patientCount}
                  </div>
                  <div className="font-bold text-slate-50 md:mt-2 text-xs">
                    Patients inscrits
                  </div>
                </div>
                <div className="flex md:w-40 md:h-36 w-24 h-24 flex-col rounded-md md:px-4 md:py-4 bg-sumo-550 opacity-90 md:mt-4 mt-2 px-2 justify-center ml-2 md:ml-0">
                  <div className="font-bold md:text-5xl text-2xl text-slate-50">
                    {patientsAges && patientsAges.length
                      ? Math.floor(
                          patientsAges.reduce((a, b) => a + b, 0) /
                            patientsAges.length
                        )
                      : "-"}
                  </div>
                  <div className="font-bold text-slate-50 md:mt-2 text-xs">
                    Âge moyen
                  </div>
                </div>
                <div className="flex md:w-40 md:h-40 w-28 h-24 flex-col rounded-md md:px-4 md:py-4 bg-sumo-500 opacity-90 md:mt-4 mt-2 px-2 justify-center ml-2 md:ml-0">
                  <div className="font-bold md:text-5xl text-2xl text-slate-50">
                    {surveysAnsweredCount}
                  </div>
                  <div className="font-bold text-slate-50 md:mt-2 text-xs">
                    Questionnaires répondus
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:px-2">
              <div className="flex w-40">
                <div className="font-bold text-slate-500 mt-2 text-center">
                  Scores pré-op
                </div>
              </div>
              <div className="flex flex-row md:flex-col">
                <div className="flex md:w-40 md:h-32 w-24 h-24 flex-col rounded-md md:px-4 md:py-4 bg-sumo-500 opacity-90 md:mt-4 mt-2 px-2 justify-center">
                  <div className="font-bold md:text-5xl text-2xl text-slate-50">
                    {baselineScores &&
                    !isNaN(_.meanBy(baselineScores, "life_quality"))
                      ? (
                          100 - _.meanBy(baselineScores, "life_quality")
                        ).toPrecision(3)
                      : "-"}
                  </div>
                  <div className="font-bold text-slate-50 md:mt-2 text-xs">
                    ODI
                  </div>
                </div>
                <div className="flex md:w-40 md:h-36 w-24 h-24 flex-col rounded-md md:px-4 md:py-4 bg-sumo-550 opacity-90 md:mt-4 mt-2 px-2 justify-center ml-2 md:ml-0">
                  <div className="font-bold md:text-5xl text-2xl text-slate-50">
                    {baselineScores &&
                    !isNaN(_.meanBy(baselineScores, "physical_autonomy"))
                      ? _.meanBy(
                          baselineScores,
                          "physical_autonomy"
                        ).toPrecision(3)
                      : "-"}
                  </div>
                  <div className="font-bold text-slate-50 md:mt-2 text-xs">
                    Autonomie
                  </div>
                </div>
                <div className="flex md:w-40 md:h-40 w-28 h-24 flex-col rounded-md md:px-4 md:py-4 bg-sumo-500 opacity-90 md:mt-4 mt-2 px-2 justify-center ml-2 md:ml-0">
                  <div className="font-bold md:text-5xl text-2xl text-slate-50">
                    {baselineScores &&
                    !isNaN(_.meanBy(baselineScores, "pain_management"))
                      ? _.meanBy(baselineScores, "pain_management").toPrecision(
                          3
                        )
                      : "-"}
                  </div>
                  <div className="font-bold text-slate-50 md:mt-2 text-xs">
                    Gestion douleur
                  </div>
                </div>
              </div>
            </div>
            {rendePostOpScoreColumn()}
            <div className="md:pl-4 my-4 md:my-auto justify-center items-center w-[100%] md:w-[600px] xl:w-[800px]">
              <div className="flex w-40">
                <div className="font-bold text-slate-500 text-center">
                  Évolution des scores
                </div>
              </div>
              <Line
                data={chartData}
                options={{
                  plugins: {
                    title: {
                      display: true,
                      text: "Évolution de la qualité de vie"
                    },
                    legend: {
                      position: "top"
                    }
                  },
                  animation: {
                    duration: 1000
                  }
                }}
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default StatsStudyGroupOverview
